import React, { FormEvent } from "react";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Button } from "@twilio-paste/core/button";
import { Checkbox, Label, Input } from "@twilio-paste/core";
import { useDispatch, useSelector } from "react-redux";
import log from "loglevel";

import { Header } from "../Header";
import { NotificationBar } from "../NotificationBar";
import { titleStyles, formStyles, introStyles, fieldStyles } from "../styles/PostChatSurvey.styles";
import { AppState, EngagementPhase } from "../../store/definitions";
import { useStartNewChat } from "../../hooks/useStartNewChat";
import { contactBackend, sessionDataHandler } from "../../sessionDataHandler";
import {
    generateEmailTranscript,
    getAgentNames,
    getTranscriptData,
    getUniqueFilenames
} from "../../utils/generateTranscripts";
import { addNotification, changeEngagementPhase } from "../../store/actions/genericActions";
import { notifications } from "../../notifications";
import { Field } from "../Field";

export const PostChatSurveyPhase = () => {
    const startNewChat = useStartNewChat(); // eslint-disable-line @typescript-eslint/no-unused-vars
    const dispatch = useDispatch();

    const {
        customerName,
        preEngagementEmail,
        postChatSurveyFields,
        token,
        conversationSid,
        messages,
        users,
        transcriptConfig,
        isCoBrowserEnabled,
        autoInitConfig
    } = useSelector((state: AppState) => ({
        customerName: state.session.preEngagementData?.name,
        preEngagementEmail: state.session.preEngagementData?.email,
        postChatSurveyFields: state.config.postChatSurveyFields,
        token: state.session.token,
        conversationSid: state.session.conversationSid,
        messages: state.chat.messages,
        users: state.chat.users,
        transcriptConfig: state.config.transcript,
        isCoBrowserEnabled: state.session.syncMapItemData?.isCoBrowserEnabled,
        autoInitConfig: state.config.autoInitConfig
    }));

    const [isSubmitingData, setIsSubmitingData] = React.useState<boolean>(false);
    const [transcriptChat, setTranscriptChat] = React.useState<boolean>(false);
    const [email, setEmail] = React.useState<string | undefined>(preEngagementEmail);

    const [additionalFields, setAdditionalFields] = React.useState<Record<string, string>>({});

    const getMediaInfo = React.useCallback(async () => {
        const mediaMessages = messages?.filter((message) => message.attachedMedia);
        const mediaInfo = [];
        for (const message of mediaMessages || []) {
            for (const media of message.attachedMedia || []) {
                try {
                    const file = {
                        name: media.filename,
                        type: media.contentType,
                        size: media.size
                    } as File;
                    const url = media ? await media.getContentTemporaryUrl() : URL.createObjectURL(file);
                    mediaInfo.push({ url, filename: media.filename, type: media.contentType });
                } catch (e) {
                    log.error(`Failed downloading message attachment: ${e}`);
                }
            }
        }
        return mediaInfo;
    }, [messages]);

    const submitData = React.useCallback(async () => {
        // If user wants the chat to be transcribed
        if (transcriptChat === false) {
            await contactBackend("/postChatSurvey", {
                fields: {
                    email: preEngagementEmail,
                    ...additionalFields
                },
                token,
                conversationSid,
                customerCoBrowserAccess: isCoBrowserEnabled
            });
        } else {
            if (!email || email.trim().length === 0) {
                throw new Error(`You need to fillup the email field first`);
            }

            const transcriptData = getTranscriptData(messages, users);
            const uniqueFilenames = getUniqueFilenames(transcriptData);
            const agentNames = getAgentNames(customerName, transcriptData);
            const mediaInfo = await getMediaInfo();
            const transcript = generateEmailTranscript(customerName, agentNames, transcriptData);

            await contactBackend("/postChatSurvey", {
                recipientAddress: email,
                subject: transcriptConfig?.emailSubject?.(agentNames),
                text: transcriptConfig?.emailContent?.(customerName, transcript),
                mediaInfo,
                uniqueFilenames,
                fields: {
                    email: preEngagementEmail,
                    ...additionalFields
                },
                token,
                conversationSid,
                customerCoBrowserAccess: isCoBrowserEnabled
            });
        }
    }, [
        transcriptChat,
        email,
        additionalFields,
        token,
        conversationSid,
        messages,
        users,
        transcriptConfig,
        customerName,
        getMediaInfo,
        preEngagementEmail,
        isCoBrowserEnabled
    ]);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setIsSubmitingData(true);

        submitData()
            .then(() => {
                if (autoInitConfig === undefined) {
                    return startNewChat();
                }

                // Clear the session storage as we have the conversation closed already
                sessionDataHandler.clear();
                return dispatch(changeEngagementPhase({ phase: EngagementPhase.ThankYouPhase }));
            })
            .catch((error) => {
                dispatch(
                    addNotification(notifications.failedSubmitPostChatSurveyNotification((error as Error).message))
                );
            });
    };

    const renderOptionalFields = React.useCallback(() => {
        if (postChatSurveyFields === undefined || postChatSurveyFields.length === 0) {
            return null;
        }

        return postChatSurveyFields.map((field) => (
            <Field
                boxStyle={fieldStyles}
                key={field.name}
                field={field}
                value={additionalFields?.[field.name]}
                onChange={(e) =>
                    setAdditionalFields((value) => ({
                        ...value,
                        [field.name]: e
                    }))
                }
            />
        ));
    }, [postChatSurveyFields, additionalFields]);

    const showTranscription = React.useMemo(
        () => transcriptConfig?.downloadEnabled,
        [transcriptConfig?.downloadEnabled]
    );

    const renderTranscription = () => {
        if (showTranscription) {
            return (
                <>
                    <Box {...fieldStyles}>
                        <Checkbox
                            checked={transcriptChat}
                            id="email-transcript"
                            name="email-transcript"
                            data-testid="post-chat-survey-email-transcript"
                            onChange={(e) => setTranscriptChat(e.target.checked)}
                        >
                            Would you like a transcript of this chat?
                        </Checkbox>
                    </Box>

                    <Box {...fieldStyles}>
                        <Label htmlFor="email">Email address to send the transcription to</Label>
                        <Input
                            type="email"
                            placeholder="Please enter your email address"
                            name="email"
                            id="email"
                            data-testid="post-chat-survey-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={!transcriptChat}
                        />
                    </Box>
                </>
            );
        }

        return (
            <Box {...fieldStyles}>
                <Label htmlFor="email">Email address</Label>
                <Input
                    type="email"
                    placeholder="Please enter your email address"
                    name="email"
                    id="email"
                    data-testid="post-chat-survey-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </Box>
        );
    };

    return (
        <>
            <Header customTitle="Post Chat Survey" />
            <NotificationBar />
            <Box as="form" data-testid="post-chat-survey" onSubmit={handleSubmit} {...formStyles}>
                <Text {...titleStyles} as="h3">
                    Thank you for contacting us
                </Text>

                <Text {...introStyles} as="p">
                    Please provide some information about our assistance.
                </Text>

                {renderTranscription()}

                {renderOptionalFields()}

                <Button
                    variant="primary"
                    type="submit"
                    loading={isSubmitingData}
                    data-testid="post-chat-survey-start-new-chat"
                >
                    Submit Data
                </Button>
            </Box>
        </>
    );
};
